import Tooltip from "rc-tooltip";
import { useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import {
  useCurrentAccess,
  useGetOrganizationUsersQuery,
  useGetScopeNodesQuery,
  useGetSeatCapsQuery,
  useResendInvitationMutation,
  useSetOrganizationUserActivationMutation,
} from "../../redux/api/org-user/orgUserAPI";
import {
  AccessUnavailableState,
  NoAccessAssignedState,
  RoleRestrictionState,
} from "../../shared/components/access-state-card/access-states";
import { EInviteChannel } from "../../shared/oversight-core/enums/invite-channel";
import { EOrgUserRole } from "../../shared/oversight-core/enums/org-user-role";
import { EOrgUserStatus } from "../../shared/oversight-core/enums/org-user-status";
import { IHttpError } from "../../shared/oversight-core/interfaces/http-errror";
import { IOrgUser } from "../../shared/oversight-core/interfaces/org-user";
import AppDropDown from "../../shared/oversight-core/ui-elements/app-dropdown/app-drop-down";
import AppSelect, {
  Option,
} from "../../shared/oversight-core/ui-elements/app-select/app-select";
import AppButton from "../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import DataTable from "../../shared/oversight-core/ui-elements/data-table/data-table";
import ModalContainer from "../../shared/oversight-core/ui-elements/modal-container/modal-container";
import Pagination from "../../shared/oversight-core/ui-elements/pagination/pagination";
import Search from "../../shared/oversight-core/ui-elements/search/search";
import SpinnerModal from "../../shared/oversight-core/ui-elements/spinner/spinner";
import {
  canManageUsers,
  getOrgUserStatusInfo,
  getRoleLabel,
  isSeatPoolFull,
  orgUserRoleLabels,
} from "../../shared/oversight-core/utils/get-org-user-info";
import {
  showErrorMessage,
  showSuccessMessage,
  showWarningMessage,
} from "../../shared/oversight-core/utils/toast";
import AddUserModal from "./components/add-user-modal/add-user-modal";
import ManageAccessModal from "./components/manage-access-modal/manage-access-modal";
import UserAccessCell from "./components/user-access-cell/user-access-cell";
import UserStatus from "./components/user-status/user-status";
import { toAccessTargets, toOrgUser } from "./users-helpers";
import styles from "./users.module.scss";

interface IUserFilters {
  searchValue: string;
  role: EOrgUserRole | "";
  status: EOrgUserStatus | "";
  pageNumber: number;
}

const initialFilters: IUserFilters = {
  searchValue: "",
  role: "",
  status: "",
  pageNumber: 0,
};

const PAGE_SIZE = 10;

const roleOptions: Option[] = [
  { value: "", label: "All Roles" },
  ...Object.values(EOrgUserRole).map((role) => ({
    value: role,
    label: orgUserRoleLabels[role],
  })),
];

const statusOptions: Option[] = [
  { value: "", label: "All Statuses" },
  ...Object.values(EOrgUserStatus).map((status) => ({
    value: status,
    label: getOrgUserStatusInfo(status).label,
  })),
];

const getErrorMessage = (error: unknown) =>
  (error as IHttpError)?.message || "Sorry, an error occurred.";

const Users = () => {
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState<IUserFilters>({ ...initialFilters });
  const [manageAccessUser, setManageAccessUser] = useState<IOrgUser>();
  const [userToDisable, setUserToDisable] = useState<IOrgUser>();
  const [showAddUser, setShowAddUser] = useState(false);

  const {
    data: currentAccess,
    isLoading: isLoadingAccess,
    error: accessError,
  } = useCurrentAccess();
  const tenantId = currentAccess?.tenantId ?? "";
  const canManage = canManageUsers(currentAccess);
  const skipTenantQueries = !tenantId || !canManage;

  const {
    data: usersData,
    isFetching: isFetchingUsers,
    error: usersError,
  } = useGetOrganizationUsersQuery(
    {
      tenantId,
      search: filters.searchValue.trim() || undefined,
      role: filters.role || undefined,
      status: filters.status || undefined,
      pageNumber: filters.pageNumber,
      pageSize: PAGE_SIZE,
    },
    { skip: skipTenantQueries },
  );
  const { data: seatCapsData } = useGetSeatCapsQuery(tenantId, {
    skip: skipTenantQueries,
  });
  // Fetched once and reused by every access dialog.
  const { data: scopeNodesData, isFetching: isFetchingScopeNodes } =
    useGetScopeNodesQuery(tenantId, { skip: skipTenantQueries });

  const [setActivation, { isLoading: isChangingActivation }] =
    useSetOrganizationUserActivationMutation();
  const [resendInvitation, { isLoading: isResendingInvitation }] =
    useResendInvitationMutation();

  const users = useMemo(
    () => usersData?.users.elements.map(toOrgUser) ?? [],
    [usersData],
  );
  const accessTargets = useMemo(
    () => toAccessTargets(scopeNodesData?.scopeNodes ?? []),
    [scopeNodesData],
  );

  const seatCaps = seatCapsData?.seatCaps;
  // A full user pool still leaves room for guests (users whose grants are all View
  // redacted), so Add User only closes when both pools are full. The dialog checks the
  // pool that matches the grants being added.
  const isAllSeatsFull =
    !!seatCaps &&
    isSeatPoolFull(seatCaps.maxUserSeats, seatCaps.occupiedUserSeats) &&
    isSeatPoolFull(seatCaps.maxGuestSeats, seatCaps.occupiedGuestSeats);

  const changeActivation = (user: IOrgUser, active: boolean) =>
    setActivation({ tenantId, userId: user.identity, active })
      .unwrap()
      .then(() =>
        showSuccessMessage(
          `${user.name} ${active ? "can sign in again" : "was disabled"}`,
        ),
      )
      .catch((error) => showErrorMessage(getErrorMessage(error)));

  const sendInvitation = (user: IOrgUser) => {
    const channel = user.email ? EInviteChannel.EMAIL : EInviteChannel.MOBILE;
    resendInvitation({ userId: user.identity, channel })
      .unwrap()
      .then(() =>
        showSuccessMessage(
          `Invitation sent to ${user.email ?? user.mobile ?? user.name}`,
        ),
      )
      .catch((error) => showErrorMessage(getErrorMessage(error)));
  };

  // Disable and Enable never appear on the caller's own row; Enable only for inactive rows.
  const getMenuItems = (user: IOrgUser) => {
    const isOwnRow = user.identity === currentAccess?.userId;
    return [
      { text: "Manage Access", onClick: () => setManageAccessUser(user) },
      ...(isOwnRow
        ? []
        : user.status === EOrgUserStatus.INACTIVE
          ? [{ text: "Enable", onClick: () => changeActivation(user, true) }]
          : [{ text: "Disable", onClick: () => setUserToDisable(user) }]),
      ...(user.status === EOrgUserStatus.INVITED
        ? [{ text: "Resend Invitation", onClick: () => sendInvitation(user) }]
        : []),
    ];
  };

  if (isLoadingAccess) {
    return <SpinnerModal show />;
  }

  // Fail closed: without a loaded access record the role can't be checked.
  if (!currentAccess) {
    return (
      <AccessUnavailableState message={(accessError as IHttpError)?.message} />
    );
  }

  // Every role but organization and platform admins gets a 401 from the user endpoints.
  if (!canManage || (usersError as IHttpError)?.status === 401) {
    return (
      <RoleRestrictionState roleLabel={getRoleLabel(currentAccess.role)} />
    );
  }

  if (!tenantId) {
    return <NoAccessAssignedState />;
  }

  return (
    <div
      className={`${styles.usersListOverflow} container-white position-relative py-4`}
    >
      <div className={styles.toolbar}>
        <Row className="align-items-center gy-3">
          <Col className="col-12 col-md-6 col-xl-3">
            <Search
              placeholder="Search name or email"
              value={searchText}
              handleSearchChange={(text) => setSearchText(text)}
              onSearch={() =>
                setFilters((ps) => ({
                  ...ps,
                  searchValue: searchText,
                  pageNumber: 0,
                }))
              }
            />
          </Col>
          <Col className="col-6 col-md-3 col-xl-2">
            <AppSelect
              options={roleOptions}
              selectedValue={roleOptions.find((o) => o.value === filters.role)}
              onChangeOption={(option) =>
                setFilters((ps) => ({
                  ...ps,
                  role: option.value as EOrgUserRole | "",
                  pageNumber: 0,
                }))
              }
              fontSize="12px"
            />
          </Col>
          <Col className="col-6 col-md-3 col-xl-2">
            <AppSelect
              options={statusOptions}
              selectedValue={statusOptions.find(
                (o) => o.value === filters.status,
              )}
              onChangeOption={(option) =>
                setFilters((ps) => ({
                  ...ps,
                  status: option.value as EOrgUserStatus | "",
                  pageNumber: 0,
                }))
              }
              fontSize="12px"
            />
          </Col>
          <Col className="col-auto">
            <p
              className="mb-0 text-primary font-size-12 font-weight-500 cursor-pointer"
              onClick={() => {
                setFilters({ ...initialFilters });
                setSearchText("");
              }}
            >
              Reset Filters
            </p>
          </Col>
          <Col className="col-auto ms-auto">
            {isAllSeatsFull ? (
              <Tooltip
                placement="bottom"
                trigger={["hover"]}
                overlay={
                  <div className="font-size-12">
                    All seats are in use: {seatCaps?.occupiedUserSeats} of{" "}
                    {seatCaps?.maxUserSeats} user seats and{" "}
                    {seatCaps?.occupiedGuestSeats} of {seatCaps?.maxGuestSeats}{" "}
                    guest seats
                  </div>
                }
              >
                {/* A disabled button fires no hover events, so the wrapper carries the tooltip. */}
                <span className="d-inline-block">
                  <AppButton
                    text="Add User"
                    iconName="add"
                    className="px-4"
                    disabled
                  />
                </span>
              </Tooltip>
            ) : (
              <AppButton
                text="Add User"
                iconName="add"
                className="px-4"
                onClick={() => setShowAddUser(true)}
              />
            )}
          </Col>
        </Row>
      </div>
      {users.length > 0 ? (
        <>
          <DataTable
            headers={[
              { id: 1, columnName: "Name" },
              { id: 2, columnName: "Role" },
              { id: 3, columnName: "Access" },
              { id: 4, columnName: "Status" },
              { id: 5, columnName: "" },
            ]}
            data={users.map((user) => ({
              data: [
                <div key="name" className={styles.nameCell}>
                  <div className="text-dark font-size-13 font-weight-600">
                    {user.name || "-"}
                  </div>
                  <div className="text-light font-size-12">
                    {user.email ?? user.mobile}
                  </div>
                </div>,
                <div key="role" className="font-size-13 text-nowrap">
                  {orgUserRoleLabels[user.role] ?? user.role}
                </div>,
                <UserAccessCell
                  key="access"
                  grants={user.grants}
                  status={user.status}
                />,
                <UserStatus key="status" status={user.status} />,
                <div key="actions" className="d-flex justify-content-end">
                  <AppDropDown
                    showBackgroundAndBorder={false}
                    items={getMenuItems(user)}
                  />
                </div>,
              ],
            }))}
          />
          <Pagination
            itemsPerPage={PAGE_SIZE}
            length={usersData?.users.totalElements ?? 0}
            currentPage={filters.pageNumber + 1}
            updateCurrentPage={(pn) =>
              setFilters((ps) => ({ ...ps, pageNumber: pn - 1 }))
            }
          />
        </>
      ) : (
        !isFetchingUsers && (
          <div className="container-dash mt-4">
            <Col
              className={`text-center font-size-12 ${
                usersError ? "text-danger" : "text-light"
              }`}
            >
              {usersError
                ? getErrorMessage(usersError)
                : "There are no users available for the selected filters."}
            </Col>
          </div>
        )
      )}
      <AddUserModal
        show={showAddUser}
        tenantId={tenantId}
        organizationName={
          currentAccess?.tenantName ?? seatCaps?.tenantName ?? ""
        }
        seatCaps={seatCaps}
        accessTargets={accessTargets}
        isLoadingTargets={isFetchingScopeNodes}
        onClose={() => setShowAddUser(false)}
        onCreated={(response, name) => {
          setShowAddUser(false);
          if (response.invitationSent) {
            showSuccessMessage(`${name} added and invited`);
          } else {
            // The account exists; only the invitation failed, so it can be resent.
            showWarningMessage(
              `${name} was added, but the invitation couldn't be sent. Use Resend Invitation from their row.`,
            );
          }
        }}
      />
      <ManageAccessModal
        show={!!manageAccessUser}
        user={manageAccessUser}
        tenantId={tenantId}
        currentUserId={currentAccess?.userId}
        accessTargets={accessTargets}
        isLoadingTargets={isFetchingScopeNodes}
        onClose={() => setManageAccessUser(undefined)}
        onSaved={() => {
          setManageAccessUser(undefined);
          showSuccessMessage("Access saved");
        }}
      />
      <ModalContainer
        show={!!userToDisable}
        title={`Disable ${userToDisable?.name ?? "user"}?`}
        size="modal-md"
        confirmButtonText="Disable"
        confirmButtonVariant="red"
        isLoading={isChangingActivation}
        onClose={() => setUserToDisable(undefined)}
        onCancel={() => setUserToDisable(undefined)}
        onConfirm={() => {
          if (userToDisable) {
            changeActivation(userToDisable, false).finally(() =>
              setUserToDisable(undefined),
            );
          }
        }}
      >
        <div className="text-light font-size-14">
          They won't be able to sign in. Their access is kept, so you can enable
          them again later.
        </div>
      </ModalContainer>
      <SpinnerModal
        show={isFetchingUsers || isChangingActivation || isResendingInvitation}
      />
    </div>
  );
};

export default Users;
